import { type ReactNode } from "react";

const iconUrls = import.meta.glob("@/assets/icons/*.svg", {
    eager: true,
    query: "?url",
    import: "default",
}) as Record<string, string>;

const iconRaw = import.meta.glob("@/assets/icons/*.svg", {
    eager: true,
    query: "?raw",
    import: "default",
}) as Record<string, string>;

/** Resolve icon name to its imported URL (for img fallback). */
function resolveUrl(name: string): string | undefined {
    for (const [path, url] of Object.entries(iconUrls)) {
        if (path.endsWith(`/${name}.svg`)) return url;
    }
    return undefined;
}

/** Resolve icon name to its raw SVG string (for inline rendering). */
function resolveRaw(name: string): string | undefined {
    for (const [path, raw] of Object.entries(iconRaw)) {
        if (path.endsWith(`/${name}.svg`)) return raw;
    }
    return undefined;
}

interface IconProps {
    /** Icon file name without extension, e.g. "back-arrow" */
    name: string;
    /** Width & height in px (default 24) */
    size?: number;
    className?: string;
    /** When true, renders SVG inline so currentColor works for color inheritance */
    inline?: boolean;
    /** When true, applies animate-spin (for loading spinners) */
    spinning?: boolean;
}

export default function Icon({ name, size = 24, className = "", inline = false, spinning = false }: IconProps): ReactNode {
    const spinClass = spinning ? "animate-spin" : "";
    if (inline) {
        const raw = resolveRaw(name);
        if (!raw) return null;

        return (
            <span
                className={`inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full ${spinClass} ${className}`}
                style={{ width: size, height: size }}
                dangerouslySetInnerHTML={{ __html: raw }}
            />
        );
    }

    const url = resolveUrl(name);
    if (!url) return null;

    return (
        <img
            src={url}
            alt=""
            width={size}
            height={size}
            className={`${spinClass} ${className}`}
            draggable={false}
        />
    );
}
